import { normalizeJourneyStage, validateJourneyStageExplanation } from './journeyStageExplanation.js';

const ED_STAGE_EXPLANATIONS = Object.freeze({
  arrival: Object.freeze({
    roleIds: Object.freeze([
      'staff_nurse',
      'healthcare_assistant',
      'resident_doctor_previously_referred_to_as_junior_doctor',
      'advanced_care_practitioner',
    ]),
    explanation: Object.freeze({
      whatHappens: 'When you arrive, the team will work out how urgently you need to be assessed. A clinician may ask what brought you to the Emergency Department. They may check your pulse, blood pressure, breathing and oxygen level. They will ask about pain or other immediate concerns.',
      whyItMayHappen: 'This first assessment helps the team identify people who may need urgent treatment and decide the safest place for each person to wait or receive care. This is why people are not always seen in the order they arrived.',
      whatTeamIsDoing: 'The team is looking for signs that need immediate attention, recording a starting set of observations and deciding what should happen next.',
      variationNote: 'Your first assessment may be very brief or more detailed depending on how unwell you seem. Some people are taken straight to a treatment or resuscitation area, while others wait for a fuller assessment.',
      nextSteps: Object.freeze([
        Object.freeze({ id: 'assessment', title: 'Further assessment and treatment', stageId: 'assessment-treatment', when: 'For most people after the first assessment' }),
        Object.freeze({ id: 'urgent-care', title: 'More urgent treatment or monitoring', stageId: 'assessment-treatment', when: 'If the team is concerned you need immediate care' }),
      ]),
    }),
  }),
  'assessment-treatment': Object.freeze({
    roleIds: Object.freeze([
      'resident_doctor_previously_referred_to_as_junior_doctor',
      'staff_nurse',
      'advanced_care_practitioner',
      'physician_associate',
      'radiographer',
      'pharmacist',
    ]),
    explanation: Object.freeze({
      whatHappens: 'A clinician may ask more questions about your health and examine you. They will review your observations. Depending on your symptoms, you may have blood tests, an ECG or a scan. Treatment may start before every result is available. This can include pain relief, fluids, medicines or oxygen. You may be reassessed while the team waits for results or checks how you respond.',
      whyItMayHappen: 'The aim is to understand what may be causing your symptoms, treat problems that cannot safely wait and gather enough information to decide whether you can go home or need more hospital care.',
      whatTeamIsDoing: 'The team is combining your history, examination, observations, test results and response to treatment. They may discuss your care with a senior Emergency Department clinician or ask another specialty team to review you.',
      variationNote: 'Not everyone needs blood tests, a scan, monitoring or the same treatment. Some results are available quickly and others take longer. A specialist review can also add another wait because that team may be caring for patients elsewhere in the hospital.',
      nextSteps: Object.freeze([
        Object.freeze({ id: 'reassess', title: 'Reassessment or further tests', stageId: 'assessment-treatment', when: 'If the team needs more information or wants to see how you respond to treatment' }),
        Object.freeze({ id: 'specialty-review', title: 'Review by another hospital team', stageId: 'assessment-treatment', when: 'If specialist advice or treatment may be needed' }),
        Object.freeze({ id: 'decision', title: 'A decision about what happens next', stageId: 'next', when: 'When the team has enough information to make a safe plan' }),
      ]),
    }),
  }),
  next: Object.freeze({
    roleIds: Object.freeze([
      'resident_doctor_previously_referred_to_as_junior_doctor',
      'staff_nurse',
      'pharmacist',
    ]),
    explanation: Object.freeze({
      whatHappens: 'When the team has enough information, they will explain the plan. You may be able to go home or return for follow-up. You may stay for more observation or move to another assessment area. Some people are transferred or admitted to a ward.',
      whyItMayHappen: 'The next step depends on what the team has found, how you are feeling, whether treatment has helped and whether you need care or monitoring that cannot safely be provided at home.',
      whatTeamIsDoing: 'The team is making sure there is a safe plan, arranging medicines or follow-up where needed, and handing over relevant information if another team or ward will take over your care.',
      variationNote: 'If you are admitted, there can be a delay between the decision to admit and moving to a ward while a suitable bed is arranged. If you go home, the team should explain what to do next and when to seek further help if things change.',
      nextSteps: Object.freeze([
        Object.freeze({ id: 'home', title: 'Going home', description: 'You may receive advice, medicines, follow-up arrangements or safety-netting information.', when: 'If the team thinks you can safely continue your care outside hospital' }),
        Object.freeze({ id: 'observation', title: 'Further observation or ambulatory care', description: 'You may stay longer for monitoring, repeat assessment or a planned review.', when: 'If more time or another review is needed before a final decision' }),
        Object.freeze({ id: 'transfer', title: 'Transfer or specialty care', description: 'Another service may take over part or all of your care.', when: 'If you need care from a specialist team or at another site' }),
        Object.freeze({ id: 'admission', title: 'Admission to a ward', description: 'A ward team will receive a handover and continue your care once a suitable bed is available.', when: 'If you need ongoing hospital treatment, monitoring or support' }),
      ]),
    }),
  }),
});

function patientSummary(explanation) {
  return [
    explanation?.whatHappens,
    explanation?.whyItMayHappen,
    explanation?.whatTeamIsDoing,
    explanation?.variationNote,
  ].filter(Boolean).join(' ');
}

export function applyEmergencyDepartmentJourneyExplanation(stages = []) {
  if (stages.length !== 3) return stages;
  const knownStageIds = stages.map((stage) => stage.id);
  return stages.map((stage) => {
    const enrichment = ED_STAGE_EXPLANATIONS[stage.id];
    if (!enrichment) return stage;
    const enriched = normalizeJourneyStage({
      ...stage,
      ...enrichment,
      description: patientSummary(enrichment.explanation),
    });
    const errors = validateJourneyStageExplanation(enriched, knownStageIds);
    if (errors.length) throw new Error(`Emergency Department stage ${stage.id}: ${errors.join('; ')}`);
    return enriched;
  });
}

export { ED_STAGE_EXPLANATIONS };
